import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, Router, RouterStateSnapshot } from '@angular/router';
import { PublicLinkService } from '@simOn/public-link';
import { isSpaceSectionAllowed, PUBLIC_LINK_SPACE_FALLBACK } from '@simOn/public-link/models';

const SPACE_ROUTE_PATH = 'space/:apartmentId';

/**
 * Allowlist for public link visitors: only the paths named in `isSpaceSectionAllowed` are reachable under
 * `space/:apartmentId`; any other URL (typed by hand or linked from a hidden menu) lands on the 3D walk.
 * The check looks at the deepest route of the navigation, so it gives the same answer for every child
 * activated on the way down. Logged users are never affected.
 */
export const publicLinkSpaceSectionGuardFn: CanActivateChildFn = (
  childRoute: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const publicLink = inject(PublicLinkService);
  const router = inject(Router);
  if (!publicLink.isPublicLinkModeActive()) return true;

  const space = findSpaceRoute(childRoute);
  if (!space) return true;
  if (isSpaceSectionAllowed(pathUnderSpace(space, state), publicLink.access())) return true;
  return router.createUrlTree(['space', space.params['apartmentId'], PUBLIC_LINK_SPACE_FALLBACK]);
};

/** The `space/:apartmentId` ancestor of `route`, or null outside the space tree. */
function findSpaceRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot | null {
  let current: ActivatedRouteSnapshot | null = route;
  while (current) {
    if (current.routeConfig?.path === SPACE_ROUTE_PATH) return current;
    current = current.parent;
  }
  return null;
}

/** Route config paths from the space route down to the leaf of the navigation, joined (`dashboard/devices`). */
function pathUnderSpace(space: ActivatedRouteSnapshot, state: RouterStateSnapshot): string {
  // `space` comes from the child's parent chain, while the leaf is reached through the state's firstChild chain;
  // walk the state tree to find the same space node and continue to the leaf from there.
  let node: ActivatedRouteSnapshot | null = state.root;
  while (node && node.routeConfig?.path !== SPACE_ROUTE_PATH) node = node.firstChild;
  const segments: string[] = [];
  for (let child = node?.firstChild ?? null; child; child = child.firstChild) {
    const path = child.routeConfig?.path;
    if (path) segments.push(path);
  }
  return segments.join('/');
}
